/**
 * A single rAF-coalesced repaint scheduler — SketchPad's one repaint *authority*.
 *
 * Before this existed, SketchPad had no single place that decided "repaint now": the
 * committed-stroke canvas was redrawn from inside a `useEffect(() => scheduleRedraw(),
 * [items])`, and a *separate* long-lived `ResizeObserver` callback (bound once, on mount,
 * via a `useEffect(() => { ... }, [])`) called its own copy of `scheduleRedraw` — a closure
 * captured from whichever render happened to be current when the effect ran. Because that
 * effect never re-ran, its `scheduleRedraw` (and the `items` it read) was frozen at mount
 * time, almost always before Yjs/IndexedDB had hydrated anything. Every later resize (an
 * iOS Safari toolbar hide/show, a keyboard opening, an orientation change) called that stale
 * closure, which both resized the canvas (clearing its bitmap — that's the HTML canvas
 * spec, not a bug) *and* repainted it with the empty/stale stroke list from mount, wiping
 * out real strokes. That was the dominant cause of "the drawing disappears sometimes" —
 * see docs/DECISIONS.md 2026-08-02.
 *
 * `RepaintController` fixes the *scheduling* half of that: one coalesced `schedule()` that
 * can be called from as many triggers as needed (the Y.Array observer, doc-load/hydration,
 * a resize, `visibilitychange`/`pageshow`) without ever piling up more than one paint per
 * frame, plus a synchronous `flush()` for the one case that cannot wait a frame — a resize
 * must resize the backing store and repaint atomically, or the user sees a cleared canvas
 * for one frame. The *staleness* half is fixed by never letting the `paint` callback close
 * over render-scoped values in the first place — `SketchPadPage.tsx` builds `paint` from
 * refs only (the live `Y.Array`, the live canvas context, the live size), so there is no
 * "current render" for it to go stale relative to.
 *
 * Kept dependency-free (no DOM, no React) so it is unit-testable in `apps/web`'s Node-only
 * vitest environment (no jsdom) with a fake, manually-driven frame source.
 */

export interface RepaintFrameSource {
  requestFrame: (cb: () => void) => number;
  cancelFrame: (handle: number) => void;
}

const defaultFrameSource: RepaintFrameSource = {
  requestFrame: (cb) => requestAnimationFrame(cb),
  cancelFrame: (handle) => cancelAnimationFrame(handle),
};

export interface RepaintController {
  /** Coalesced: any number of calls before the pending frame fires result in exactly one
   * `paint()` call, using whatever is current *at paint time* (not at schedule time). */
  schedule(): void;
  /** Paints immediately, synchronously, cancelling any frame already pending first (so a
   * frame that was about to fire doesn't also repaint microseconds later). For triggers
   * that cannot afford to wait a frame — see the resize case in the module doc comment. */
  flush(): void;
  /** Drops a pending frame without painting. For teardown only. */
  cancel(): void;
}

export function createRepaintController(
  paint: () => void,
  frameSource: RepaintFrameSource = defaultFrameSource,
): RepaintController {
  let pending: number | null = null;

  const cancel = (): void => {
    if (pending === null) return;
    frameSource.cancelFrame(pending);
    pending = null;
  };

  const schedule = (): void => {
    if (pending !== null) return;
    pending = frameSource.requestFrame(() => {
      pending = null;
      paint();
    });
  };

  const flush = (): void => {
    cancel();
    paint();
  };

  return { schedule, flush, cancel };
}
